import { FC } from "react";
import { Layers, CheckCircle, Clock, AlertTriangle, Sparkles, Shield } from "lucide-react";
import ShredItem from "@/components/architecture/ShredItem";
import GenerateMoreShredsCard from '@/components/ui/GenerateMoreShredsCard';
import Button from "@/components/ui/Button/index";
import { ShredResponse } from "@/services/MicroServiceService";

interface FeatureDevelopmentStepProps {
    dataModelShred: ShredResponse | undefined;
    featureShreds: ShredResponse[];
    incompleteShreds: ShredResponse[];
    completeShreds: ShredResponse[];
    generatingShredId: string | null;
    isAnyShredGenerating: boolean;
    isModelReady: boolean;
    isModelValidated: boolean;
    loading: boolean;
    serviceStatus: "IDLE" | "GENERATING_SHREDS" | "FAILED";
    generateShredModel: boolean;
    setGenerateShredModel: (value: boolean) => void;
    projectId: string;
    serviceId: string;
    onGenerateSpecs: (shredId: string) => void;
    onExplore: (shredId: string) => void;
    onDelete: (shredId: string) => void;
    onGenerateShred: () => void;
}

const FeatureDevelopmentStep: FC<FeatureDevelopmentStepProps> = ({
    dataModelShred,
    featureShreds,
    incompleteShreds,
    completeShreds,
    generatingShredId,
    isAnyShredGenerating,
    isModelReady,
    isModelValidated,
    loading,
    serviceStatus,
    generateShredModel,
    setGenerateShredModel,
    projectId,
    serviceId,
    onGenerateSpecs,
    onExplore,
    onDelete,
    onGenerateShred
}) => {
    const getStepStatus = () => {
        if (!dataModelShred) {
            return {
                icon: <Clock className="w-4 h-4"/>,
                text: "WAITING",
                bgColor: "bg-gray-100",
                textColor: "text-gray-600",
                borderColor: "border-gray-300",
                headerBg: "bg-gray-50 border-gray-200"
            };
        }

        if (!isModelValidated) {
            return {
                icon: <Shield className="w-4 h-4"/>,
                text: "MODEL VALIDATION REQUIRED",
                bgColor: "bg-yellow-100",
                textColor: "text-yellow-800",
                borderColor: "border-yellow-300",
                headerBg: "bg-yellow-50 border-yellow-200"
            };
        }

        if (incompleteShreds.length > 0) {
            return {
                icon: <Clock className="w-4 h-4"/>,
                text: `${incompleteShreds.length} TO COMPLETE`,
                bgColor: "bg-orange-100",
                textColor: "text-orange-800",
                borderColor: "border-emerald-200",
                headerBg: "bg-gradient-to-r from-emerald-50 to-emerald-100 border-emerald-200"
            };
        }

        if (featureShreds.length === 0) {
            return {
                icon: <AlertTriangle className="w-4 h-4"/>,
                text: "NO FEATURES",
                bgColor: "bg-blue-100",
                textColor: "text-blue-800",
                borderColor: "border-emerald-200",
                headerBg: "bg-gradient-to-r from-emerald-50 to-emerald-100 border-emerald-200"
            };
        }

        return {
            icon: <CheckCircle className="w-4 h-4"/>,
            text: "ALL COMPLETE",
            bgColor: "bg-green-100",
            textColor: "text-green-800",
            borderColor: "border-emerald-200",
            headerBg: "bg-gradient-to-r from-green-50 to-green-100 border-green-200"
        };
    };

    const stepStatus = getStepStatus();
    const isStepDisabled = !dataModelShred || !isModelValidated;

    return (
        <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${
            isStepDisabled ? 'opacity-60' : ''
        } ${stepStatus.borderColor}`}>
            <div className={`px-6 py-4 border-b ${stepStatus.headerBg}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-lg ${
                            isStepDisabled
                                ? 'bg-gray-400'
                                : completeShreds.length > 0
                                    ? 'bg-green-500'
                                    : 'bg-emerald-500'
                        }`}>
                            {isStepDisabled ? <Shield className="w-6 h-6"/> :
                             completeShreds.length > 0 ? <CheckCircle className="w-6 h-6"/> : <Layers className="w-6 h-6"/>}
                        </div>
                        <div>
                            <div className="flex items-center space-x-3">
                                <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                    isStepDisabled
                                        ? 'bg-gray-100 text-gray-600'
                                        : completeShreds.length > 0
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-emerald-100 text-emerald-800'
                                }`}>
                                    STEP 2
                                </span>
                                <h3 className="text-xl font-bold text-gray-900">Feature Development</h3>
                            </div>
                            <p className="text-gray-600 mt-1">
                                {isStepDisabled
                                    ? "Validate data model first to unlock feature development"
                                    : "Build business logic and functionality modules"
                                }
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col items-end">
                        <div className={`flex items-center space-x-2 px-3 py-2 ${stepStatus.bgColor} ${stepStatus.textColor} rounded-lg`}>
                            {stepStatus.icon}
                            <span className="text-sm font-medium">{stepStatus.text}</span>
                        </div>
                        {!isModelValidated && dataModelShred && (
                            <p className="text-xs text-yellow-600 mt-1 text-right">
                                Validate model to continue
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="p-6">
                {featureShreds.length > 0 ? (
                    <div className="grid grid-cols-12 gap-4">
                        {featureShreds.map((shred) => (
                            <div key={shred.shredId} className="col-span-12 lg:col-span-6">
                                <ShredItem
                                    hideHeader={false}
                                    hideTowProgress={false}
                                    id={shred.shredId}
                                    title={shred.name}
                                    startDate={shred.startDate}
                                    progress={shred.progress}
                                    credit={shred.coinsUsed}
                                    generationTime={shred.generationTime}
                                    model={shred.model}
                                    project={projectId}
                                    step={shred.step}
                                    status={shred.status}
                                    summary={shred.summary}
                                    isGeneratingSpecs={generatingShredId === shred.shredId}
                                    isAnyShredGenerating={isAnyShredGenerating}
                                    isModelReady={isModelReady}
                                    isModelValidated={isModelValidated}
                                    onGenerateSpecs={() => onGenerateSpecs(shred.shredId)}
                                    onExplore={() => onExplore(shred.shredId)}
                                    onDelete={onDelete}
                                />
                            </div>
                        ))}
                        <div className="col-span-12 lg:col-span-6">
                            <GenerateMoreShredsCard
                                id={projectId}
                                serviceId={serviceId}
                                loading={loading}
                                generateShredModel={generateShredModel}
                                setGenerateShredModel={setGenerateShredModel}
                                onGenerateShred={onGenerateShred}
                                hasDataModel={!!dataModelShred}
                                disabled={isStepDisabled}
                            />
                        </div>
                    </div>
                ) : (
                    <div className={`text-center py-8 px-4 rounded-xl border-2 border-dashed ${
                        isStepDisabled 
                            ? 'bg-gray-50/50 border-gray-200' 
                            : 'bg-emerald-50/50 border-emerald-200'
                    }`}>
                        {isStepDisabled ? (
                            <>
                                <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4"/>
                                <h4 className="text-lg font-semibold text-gray-600 mb-2">Model Validation Required</h4>
                                <p className="text-gray-500 text-sm mb-4 max-w-md mx-auto">
                                    Please validate your data model first by clicking "Explore" on the data model shred above
                                </p>
                                <div className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium">
                                    <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
                                    Waiting for validation
                                </div>
                            </>
                        ) : (
                            <>
                                <Layers className="w-16 h-16 text-emerald-400 mx-auto mb-4"/>
                                <h4 className="text-lg font-semibold text-emerald-900 mb-2">No Feature Shreds Yet</h4>
                                <p className="text-emerald-700 text-sm mb-4 max-w-md mx-auto">
                                    Generate feature shreds to add business logic and functionality to your service
                                </p>
                                <div className="inline-flex items-center px-4 py-2 bg-emerald-100 text-emerald-800 rounded-lg text-sm font-medium mb-4">
                                    <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></div>
                                    Ready to add features
                                </div>
                                <div>
                                    <Button
                                        loading={loading}
                                        variant='default'
                                        className='bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white border-0 shadow-sm hover:shadow-md transition-all duration-200'
                                        icon={<Sparkles className="w-4 h-4"/>}
                                        onClick={onGenerateShred}
                                        disabled={loading || serviceStatus === "GENERATING_SHREDS" || isStepDisabled}
                                    >
                                        Generate Feature Shreds
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeatureDevelopmentStep;